<script setup lang="ts">
    import { computed, ref } from 'vue'
    import { useI18n } from 'vue-i18n'
    import draggable from 'vuedraggable'
    import type { SchemaField, SchemaFieldType } from 'composables'
    import { FIELD_TYPE_OPTIONS, isNestedType, createField } from 'composables'

    const props = withDefaults(
        defineProps<{
            field: SchemaField
            depth?: number
        }>(),
        {
            depth: 0,
        },
    )

    const emit = defineEmits<{
        'update:field': [field: SchemaField]
        remove: []
    }>()

    const { t: $t } = useI18n({ useScope: 'global' })

    const TYPE_ICON: Record<string, string> = {
        string: 'ri:text',
        number: 'ri:hashtag',
        integer: 'ri:hashtag',
        boolean: 'ri:toggle-line',
        object: 'ri:braces-line',
        'array/string': 'ri:list-unordered',
        'array/number': 'ri:list-ordered',
        'array/integer': 'ri:list-ordered',
        'array/boolean': 'ri:list-check',
        'array/object': 'ri:brackets-line',
    }

    const typeOptions = FIELD_TYPE_OPTIONS.map((type) => ({
        value: type,
        icon: TYPE_ICON[type] ?? 'ri:question-line',
        label: type
            .split('/')
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join('/'),
    }))

    const onUpdateKey = (key: string | undefined) => {
        emit('update:field', { ...props.field, key: key ?? '' })
    }

    const onUpdateType = (type: SchemaFieldType | undefined) => {
        if (!type) {
            return
        }
        const prevIsNested = isNestedType(props.field.type)
        const nextIsNested = isNestedType(type)
        const children =
            prevIsNested && nextIsNested ? props.field.children : []
        emit('update:field', { ...props.field, type, children })
    }

    const onUpdateDescription = (description: string | undefined) => {
        emit('update:field', { ...props.field, description: description ?? '' })
    }

    const onUpdateConstValue = (constValue: string | undefined) => {
        emit('update:field', {
            ...props.field,
            constValue: constValue || undefined,
        })
    }

    const onUpdateEnum = (values: unknown) => {
        const enumValues = (Array.isArray(values) ? values : [])
            .map((v) => String(v).trim())
            .filter((v) => v !== '')
        emit('update:field', {
            ...props.field,
            enumValues: enumValues.length > 0 ? enumValues : undefined,
        })
    }

    const childrenModel = computed({
        get: () => props.field.children,
        set: (children) => emit('update:field', { ...props.field, children }),
    })

    const onUpdateChild = (index: number, updatedChild: SchemaField) => {
        const children = props.field.children.map((c, i) =>
            i === index ? updatedChild : c,
        )
        emit('update:field', { ...props.field, children })
    }

    const onRemoveChild = (index: number) => {
        const children = props.field.children.filter((_, i) => i !== index)
        emit('update:field', { ...props.field, children })
    }

    const onAddChild = () => {
        const children = [...props.field.children, createField()]
        emit('update:field', { ...props.field, children })
    }

    const onNestedDragChange = (evt: {
        added?: { element: SchemaField; newIndex: number }
    }) => {
        if (evt.added) {
            const { element, newIndex } = evt.added
            const current = props.field.children
            if (!current.some((c) => c.id === element.id)) {
                const children = [
                    ...current.slice(0, newIndex),
                    element,
                    ...current.slice(newIndex),
                ]
                emit('update:field', { ...props.field, children })
            }
        }
    }

    const onToggleRequired = () => {
        emit('update:field', {
            ...props.field,
            required: !props.field.required,
        })
    }

    const expanded = ref(false)

    const hasDetails = computed(
        () =>
            !!props.field.description ||
            !!props.field.constValue ||
            !!props.field.enumValues?.length,
    )
</script>

<template>
    <div
        class="pk-schema-field"
        :class="{ 'pk-schema-field--expanded': expanded }">
        <div class="pk-schema-field__wrapper">
            <!-- Primary row: drag + name + type + expand -->
            <div class="pk-schema-field__row">
                <div class="pk-schema-field__drag">
                    <VvButton
                        modifiers="action-quiet"
                        class="drag-handle"
                        :title="$t('action.reorder')"
                        icon="ri:draggable" />
                </div>
                <div class="pk-schema-field__inputs">
                    <VvInputText
                        :model-value="field.key"
                        :placeholder="$t('label.name')"
                        name="key"
                        modifiers="compact"
                        class="pk-schema-field__input-name"
                        @update:model-value="onUpdateKey" />
                    <VvCombobox
                        :model-value="field.type"
                        :options="typeOptions"
                        value-key="value"
                        label-key="label"
                        name="type"
                        :unselectable="false"
                        select-hint-label=""
                        deselect-hint-label=""
                        selected-hint-label=""
                        class="pk-schema-field__input-type"
                        modifiers="compact"
                        @update:model-value="onUpdateType">
                        <template #option="{ option }">
                            <span class="pk-schema-field__type-option">
                                <VvIcon
                                    :name="option.icon"
                                    class="pk-schema-field__type-icon" />
                                <span>{{ option.label }}</span>
                            </span>
                        </template>
                        <template #value="{ selectedOptions }">
                            <span
                                v-if="selectedOptions.length"
                                class="pk-schema-field__type-option">
                                <VvIcon
                                    :name="selectedOptions[0].icon"
                                    class="pk-schema-field__type-icon" />
                                <span>{{ selectedOptions[0].label }}</span>
                            </span>
                        </template>
                    </VvCombobox>
                    <div class="relative">
                        <VvButton
                            modifiers="action-quiet"
                            :icon="
                                expanded
                                    ? 'ri:arrow-up-s-line'
                                    : 'ri:arrow-down-s-line'
                            "
                            :title="$t('label.details')"
                            :active="expanded"
                            class="pk-schema-field__expand-btn"
                            @click="expanded = !expanded" />
                        <VvBadge
                            v-if="hasDetails"
                            modifiers="gray sm"
                            class="absolute top-0 right-0 nr-2 nt-2" />
                    </div>
                </div>
            </div>

            <!-- Detail row: description + const (collapsible) -->
            <div v-if="expanded" class="pk-schema-field__details">
                <VvInputText
                    :model-value="field.description"
                    :placeholder="$t('label.description')"
                    name="description"
                    class="pk-schema-field__input-desc"
                    modifiers="compact"
                    @update:model-value="onUpdateDescription" />
                <VvInputText
                    :model-value="field.constValue"
                    :placeholder="$t('label.const')"
                    name="constValue"
                    class="pk-schema-field__input-const"
                    modifiers="compact"
                    @update:model-value="onUpdateConstValue" />
                <VvCombobox
                    v-if="!isNestedType(field.type)"
                    :model-value="field.enumValues ?? []"
                    :options="[]"
                    :placeholder="$t('label.enum')"
                    :search-placeholder="$t('action.add')"
                    :add-option-hint-label="$t('action.addHint')"
                    name="enumValues"
                    multiple
                    badges
                    badge-modifiers="action"
                    addable
                    searchable
                    class="pk-schema-field__input-enum"
                    modifiers="compact"
                    @keydown.enter.prevent
                    @update:model-value="onUpdateEnum" />
            </div>

            <!-- Nested children -->
            <div
                v-if="isNestedType(field.type)"
                class="pk-schema-field__nested">
                <draggable
                    v-model="childrenModel"
                    item-key="id"
                    handle=".drag-handle"
                    :group="{ name: 'schema-fields' }"
                    @change="onNestedDragChange">
                    <template #item="{ element, index }">
                        <PkSchemaField
                            :field="element"
                            :depth="depth + 1"
                            @update:field="onUpdateChild(index, $event)"
                            @remove="onRemoveChild(index)" />
                    </template>
                </draggable>
                <div class="pk-schema-field__nested-actions">
                    <VvButton
                        modifiers="action-quiet-small"
                        :label="$t('action.add')"
                        icon="ri:add-line"
                        icon-position="right"
                        @click="onAddChild" />
                </div>
            </div>
        </div>
        <div class="pk-schema-field__actions">
            <VvButtonGroup
                modifiers="compact vertical"
                item-modifiers="action-quiet-small">
                <VvButton
                    :title="$t('label.required')"
                    icon="ri:asterisk"
                    :active="field.required"
                    @click.stop="onToggleRequired" />
                <VvButton
                    :title="$t('action.delete')"
                    icon="ri:delete-bin-line"
                    @click.stop="$emit('remove')" />
            </VvButtonGroup>
        </div>
    </div>
</template>

<style lang="scss">
    .pk-schema-field {
        display: flex;
        border-top: 1px solid var(--color-surface-3);
        background-color: var(--color-surface);

        &__wrapper {
            display: flex;
            flex-direction: column;
            justify-content: center;
            flex-grow: 1;
            min-width: 0;
        }

        &__row {
            display: flex;
            align-items: center;
            padding-block: var(--spacing-8);
            padding-right: var(--spacing-xs);
        }

        &__drag {
            padding: var(--spacing-6);
            flex-shrink: 0;
        }

        &__inputs {
            display: flex;
            flex: 1;
            align-items: center;
            gap: var(--spacing-xs);
            min-width: 0;
        }

        &__input-name {
            flex: 1;
            min-width: 0;
            margin-bottom: 0;
        }

        &__input-type {
            min-width: 10rem;
            margin-bottom: 0;
        }

        &__type-option {
            display: flex;
            align-items: center;
            gap: var(--spacing-xs);
        }

        &__type-icon {
            width: var(--spacing-14);
            height: var(--spacing-14);
            flex-shrink: 0;
        }

        &__expand-btn {
            flex-shrink: 0;
        }

        &__details {
            display: flex;
            align-items: flex-start;
            gap: var(--spacing-xs);
            padding: 0 var(--spacing-xs) var(--spacing-8);
            padding-left: calc(var(--spacing-6) * 2 + var(--spacing-32));
        }

        &__input-desc {
            flex: 2;
            min-width: 0;
            margin-bottom: 0;
        }

        &__input-const {
            flex: 1;
            min-width: 0;
            margin-bottom: 0;
        }

        &__input-enum {
            flex: 1;
            min-width: 0;
            margin-bottom: 0;
        }

        &__actions {
            display: flex;
            flex-direction: column;
            justify-content: center;
            flex-shrink: 0;
            padding: var(--spacing-6);
            background-color: var(--color-surface-1);
            border-left: var(--border) solid var(--color-surface-3);
        }

        &__nested {
            position: relative;
            border-left: var(--spacing-4) solid var(--color-surface-1);

            &::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                bottom: 0;
                width: var(--spacing-1);
                background-color: var(--color-surface-3);
                z-index: 1;
            }

            .pk-schema-field__nested {
                border-color: var(--color-surface-2);

                .pk-schema-field__nested {
                    border-color: var(--color-surface-3);

                    .pk-schema-field__nested {
                        border-color: var(--color-surface-4);

                        .pk-schema-field__nested {
                            border-color: var(--color-surface-5);
                        }
                    }
                }
            }
        }

        &__nested-actions {
            display: flex;
            justify-content: flex-end;
            padding: var(--spacing-xs);
            border-top: var(--border) solid var(--color-surface-3);
        }
    }
</style>
